import { type ReactElement } from 'react'

import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-enterprise'
import type { Menu } from '~/components/ActionMenu'
import type { IPurchase } from '~/types/app-purchase'
import type { IConnection, IConnectionCode } from '~/types/app-user'

import ActionMenu from '~/components/ActionMenu'
import DateTimeCellRenderer from '~/components/CellRenderers/DateTimeCellRenderer'
import TextCellRenderer from '~/components/CellRenderers/TextCellRenderer'
import { AUTHORIZATIONS } from '~/const'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { strDateFilterComparator } from '~/utils/date'
import { actionsColDef, dateColDef, dateIsoColDef, textColDef } from '~/utils/table'

const ActionsCellRenderer = ({ context, data }: ICellRendererParams<IConnection | IPurchase>): ReactElement => {
  const isUserAuthorized = useIsUserAuthorized([AUTHORIZATIONS.MWM_USERS.ACCOUNT.WRITE])

  const { handleDeletePurchase, handleEditPurchase, handleGetToken, type } = context

  const castedConnectionData = type === 'connections' ? (data as IConnection) : null

  const castedPurchaseData = type === 'purchases' ? (data as IPurchase) : null

  if (context) {
    const menu: Menu =
      type === 'connections'
        ? {
            actions: [
              {
                action: () =>
                  handleGetToken({
                    appKey: castedConnectionData?.app_key,
                    deviceLanguage: castedConnectionData?.device_language,
                    deviceType: castedConnectionData?.device_type,
                    installationId: castedConnectionData?.installation_id,
                  }),
                label: 'Get token',
                type: 'default',
              },
            ],
          }
        : castedPurchaseData?.type === 'subscription'
          ? {
              actions: [
                {
                  action: () => handleEditPurchase(castedPurchaseData),
                  label: 'Edit',
                  type: 'default',
                },
                {
                  action: () => handleDeletePurchase(castedPurchaseData),
                  label: 'Delete',
                  type: 'default',
                },
              ],
            }
          : {
              actions: [
                {
                  action: () => handleDeletePurchase(castedPurchaseData),
                  label: 'Delete',
                  type: 'default',
                },
              ],
            }

    return <ActionMenu disabled={!isUserAuthorized} menu={menu} placement="top" />
  }

  return <div />
}

export const purchasesColumns: ColDef[] = [
  textColDef(TextCellRenderer, {
    field: 'app_key',
    headerName: 'Application',
  }),
  textColDef(TextCellRenderer, {
    field: 'device_type',
    headerName: 'Device type',
  }),
  textColDef(TextCellRenderer, {
    field: 'type',
    headerName: 'Type',
  }),
  textColDef(TextCellRenderer, {
    field: 'sku',
    headerName: 'Sku',
  }),
  dateColDef({
    field: 'purchased_at',
    filterParams: {
      buttons: ['clear'],
      comparator: strDateFilterComparator,
    },
    headerName: 'Purchased at',
    valueGetter: ({ data }: ValueGetterParams<IPurchase>) => data && data.purchased_at * 1000000,
  }),
  dateColDef({
    field: 'expiration_date',
    filterParams: {
      buttons: ['clear'],
      comparator: strDateFilterComparator,
    },
    headerName: 'Expiration date',
    valueGetter: ({ data }: ValueGetterParams<IPurchase>) => data && data.expiration_date * 1000000,
  }),
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export const featuresColumns: ColDef[] = [
  textColDef(TextCellRenderer, {
    field: 'appKey',
    headerName: 'Application',
  }),
  textColDef(TextCellRenderer, {
    field: 'deviceType',
    headerName: 'Device type',
  }),
  textColDef(TextCellRenderer, {
    field: 'id',
    headerName: 'Type',
  }),
]

export const credentialsColumns: ColDef[] = [
  textColDef(TextCellRenderer, {
    field: 'provider_user_id',
    headerName: 'Provider ID',
  }),
  textColDef(TextCellRenderer, {
    field: 'provider',
    headerName: 'Provider',
  }),
  dateIsoColDef({
    field: 'created_at',
    filterParams: {
      buttons: ['clear'],
      comparator: strDateFilterComparator,
    },
    headerName: 'Created at ',
  }),
  dateIsoColDef({
    field: 'updated_at',
    filterParams: {
      buttons: ['clear'],
      comparator: strDateFilterComparator,
    },
    headerName: 'Updated at',
  }),
]

export const connectionsColumns: ColDef[] = [
  textColDef(TextCellRenderer, {
    field: 'connection_id',
    headerName: 'Connection ID',
  }),
  textColDef(TextCellRenderer, {
    field: 'installation_id',
    headerName: 'Installation ID',
  }),
  textColDef(TextCellRenderer, {
    field: 'app_key',
    headerName: 'Application',
  }),
  textColDef(TextCellRenderer, {
    field: 'device_type',
    headerName: 'Device',
  }),
  textColDef(TextCellRenderer, {
    field: 'device_language',
    headerName: 'Language',
  }),
  textColDef(TextCellRenderer, {
    field: 'country',
    headerName: 'Country',
  }),
  textColDef(TextCellRenderer, {
    field: 'active',
    headerName: 'Status',
    valueGetter: ({ data }: ValueGetterParams<IConnection>): string => {
      const { active } = data || {}

      return active ? 'Active' : 'Inactive'
    },
  }),
  dateIsoColDef({
    field: 'created_at',
    filterParams: {
      buttons: ['clear'],
      comparator: strDateFilterComparator,
    },
    headerName: 'Created at',
  }),
  dateIsoColDef({
    field: 'updated_at',
    filterParams: {
      buttons: ['clear'],
      comparator: strDateFilterComparator,
    },
    headerName: 'Updated at',
  }),
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export const connectionCodesColumns: ColDef[] = [
  textColDef(TextCellRenderer, {
    field: 'code',
    headerName: 'Code',
  }),
  textColDef(TextCellRenderer, {
    field: 'code_type',
    headerName: 'Type',
    suppressSizeToFit: true,
  }),
  dateColDef({
    cellRenderer: DateTimeCellRenderer,
    field: 'created_at',
    headerName: 'Created at',
    suppressSizeToFit: false,
    valueGetter: ({ data }: ValueGetterParams<IConnectionCode, number>) => data && data.created_at * 1000000,
  }),
  dateColDef({
    cellRenderer: DateTimeCellRenderer,
    field: 'used_at',
    headerName: 'Used at',
    suppressSizeToFit: false,
    valueGetter: ({ data }: ValueGetterParams<IConnectionCode, number | null>) =>
      data && data.used_at && data.used_at * 1000000,
  }),
]
